import { useEffect, useRef } from 'react';
import notificationSound from './NotificationSound';

/**
 * Менеджер звуковых уведомлений
 * Автоматически проигрывает звук при:
 * - Назначении события службе (для служб и админа)
 * - Завершении события (для жителей)
 */
const SoundNotificationManager = ({ user, enabled = true }) => {
  const loginTimeRef = useRef(Date.now()); // Время входа пользователя
  const processedItemsRef = useRef(new Set());
  const intervalRef = useRef(null);
  
  // Добавляем лог при монтировании компонента
  console.log('[SoundNotificationManager] Component mounted, user=', user, 'enabled=', enabled);

  useEffect(() => {
    console.log('[SoundNotificationManager] useEffect triggered, user=', user, 'enabled=', enabled);
    if (!enabled || !user) {
      console.log('[SoundNotificationManager] Exiting early - enabled:', enabled, 'user:', user);
      return;
    }

    const checkForNotifications = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

        console.log(`[SoundNotificationManager] Checking notifications for ${user.role}...`);

        // Для служб и админа - проверяем новые назначенные события
        if (user.role === 'service' || user.role === 'admin') {
          const response = await fetch(`${backendUrl}/api/notifications`, {
            credentials: 'include'
          });

          if (response.ok) {
            const notifications = await response.json();
            console.log(`[Служба] Получено уведомлений: ${notifications.length}`, notifications);
            
            // Фильтруем только новые уведомления о событиях
            const newEventNotifications = notifications.filter(n => {
              const isNewEvent = n.notification_type === 'new_event';
              const isNotProcessed = !processedItemsRef.current.has(`notif_${n.id}`);
              const createdTime = new Date(n.created_at).getTime();
              const isAfterLogin = createdTime > loginTimeRef.current;
              
              console.log(`[Служба] Проверка уведомления ${n.id}: type=${n.notification_type}, processed=${!isNotProcessed}, afterLogin=${isAfterLogin}, read=${n.is_read}`);
              
              return isNewEvent && isNotProcessed && isAfterLogin && !n.is_read;
            });

            console.log(`[Служба] Новых уведомлений для звука: ${newEventNotifications.length}`);

            if (newEventNotifications.length > 0) {
              console.log(`🔔 [Служба] Новое событие назначено! Количество: ${newEventNotifications.length}`);
              notificationSound.playServiceNotificationSound();
              
              // Отмечаем как обработанные
              newEventNotifications.forEach(n => {
                processedItemsRef.current.add(`notif_${n.id}`);
              });
            }
          } else {
            console.error(`[Служба] Ошибка получения уведомлений: ${response.status}`);
          }
        }
        
        // Для жителей - проверяем уведомления о завершении
        if (user.role === 'resident') {
          const response = await fetch(`${backendUrl}/api/notifications`, {
            credentials: 'include'
          });

          if (response.ok) {
            const notifications = await response.json();
            console.log(`[Житель] Получено уведомлений: ${notifications.length}`, notifications);
            
            // Фильтруем уведомления о завершении событий
            const completionNotifications = notifications.filter(n => {
              const isCompletion = n.notification_type === 'event_completed';
              const isNotProcessed = !processedItemsRef.current.has(`notif_${n.id}`);
              const createdTime = new Date(n.created_at).getTime();
              const isAfterLogin = createdTime > loginTimeRef.current;
              
              console.log(`[Житель] Проверка уведомления ${n.id}: type=${n.notification_type}, processed=${!isNotProcessed}, afterLogin=${isAfterLogin}, read=${n.is_read}`);
              
              return isCompletion && isNotProcessed && isAfterLogin && !n.is_read;
            });

            console.log(`[Житель] Новых уведомлений для звука: ${completionNotifications.length}`);

            if (completionNotifications.length > 0) {
              console.log(`✅ [Житель] Ваше событие завершено! Количество: ${completionNotifications.length}`);
              notificationSound.playEventCreatedSound();
              
              // Отмечаем как обработанные
              completionNotifications.forEach(n => {
                processedItemsRef.current.add(`notif_${n.id}`);
              });
            }
          } else {
            console.error(`[Житель] Ошибка получения уведомлений: ${response.status}`);
          }
        }
        
      } catch (error) {
        console.error('[SoundNotificationManager] Ошибка при проверке уведомлений:', error);
      }
    };

    console.log(`[SoundNotificationManager] Инициализирован для роли: ${user.role}, первая проверка через 5 сек`);
    
    // Первая проверка через 5 секунд после загрузки
    const initialTimeout = setTimeout(() => {
      console.log('[SoundNotificationManager] Запуск первой проверки уведомлений');
      checkForNotifications();
      
      // Затем проверяем каждые 10 секунд
      intervalRef.current = setInterval(checkForNotifications, 10000);
    }, 5000);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [user, enabled]);

  return null; // Компонент не рендерит UI, только логика
};

export default SoundNotificationManager;
